/**
 * Phase 7 · Staff: employee master data, roster planning with shift
 * templates, attendance register, tip distribution and payroll runs.
 */
import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { http, ApiError } from '../api/client';
import { useAuth } from '../store/auth';
import { useUi } from '../store/ui';
import { money, dateTime } from '../lib/format';
import { DataTable, type Column } from '../components/ui/DataTable';
import { Button } from '../components/ui/Button';
import { Badge, StatusBadge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Input, Select } from '../components/ui/Input';

interface Employee { id: string; full_name: string; designation: string | null; employment_type: string; base_salary: number; hourly_rate: number; employee_no: string | null }
interface RosterEntry { id: string; employee_name?: string; work_date: string; start: string; end: string; status: string; role_key: string | null }
interface Attendance { id: string; employee_name?: string; work_date: string; check_in: string | null; check_out: string | null; status: string }
interface PayrollRun { id: string; period_start: string; period_end: string; status: string; total_net?: number; total_gross?: number }

const TABS = ['Employees', 'Roster', 'Attendance', 'Payroll'] as const;

export function StaffPage() {
  const canManage = useAuth((s) => s.user?.permissions.includes('staff:manage') ?? false);
  const canRoster = useAuth((s) => s.user?.permissions.includes('staff:roster') ?? false);
  const canPayroll = useAuth((s) => s.user?.permissions.includes('staff:payroll') ?? false);
  const toast = useUi((s) => s.toast);
  const [tab, setTab] = useState<(typeof TABS)[number]>('Employees');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [attendance, setAttendance] = useState<Attendance[]>([]);
  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [empModal, setEmpModal] = useState(false);
  const [rosterModal, setRosterModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, r, a, p] = await Promise.all([
        http.get<{ employees: Employee[] }>('/staff/employees'),
        http.get<{ entries: RosterEntry[] }>('/staff/roster').catch(() => ({ entries: [] })),
        http.get<{ attendance: Attendance[] }>('/staff/attendance').catch(() => ({ attendance: [] })),
        http.get<{ runs: PayrollRun[] }>('/staff/payroll').catch(() => ({ runs: [] })),
      ]);
      setEmployees(e.employees); setRoster(r.entries); setAttendance(a.attendance); setRuns(p.runs);
    } catch (e) {
      toast({ kind: 'error', title: 'Load failed', message: e instanceof ApiError ? e.message : String(e) });
    } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { void load(); }, [load]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try { await fn(); toast({ kind: 'success', title: ok }); await load(); }
    catch (e) { toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) }); }
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-ink">Staff</h1>
        <div className="flex gap-2">
          {canRoster && <Button variant="secondary" onClick={() => setRosterModal(true)}><Plus size={16} /> Roster shift</Button>}
          {canManage && <Button onClick={() => setEmpModal(true)}><Plus size={16} /> Employee</Button>}
        </div>
      </header>

      <div className="flex flex-wrap gap-1 rounded-lg bg-surface-1 p-1" role="tablist">
        {TABS.map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${tab === t ? 'bg-surface-0 text-ink shadow-sm' : 'text-ink-muted hover:text-ink'}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'Employees' && (
        <DataTable columns={[
          { key: 'full_name', header: 'Name', sortable: true },
          { key: 'employee_no', header: 'No.', render: (r: Employee) => r.employee_no ?? '—' },
          { key: 'designation', header: 'Role', render: (r: Employee) => r.designation ?? '—' },
          { key: 'employment_type', header: 'Type', render: (r: Employee) => r.employment_type.replace('_', ' ') },
          { key: 'hourly_rate', header: 'Hourly', render: (r: Employee) => money(r.hourly_rate) },
          { key: 'base_salary', header: 'Salary', render: (r: Employee) => money(r.base_salary) },
        ]} rows={employees} loading={loading} />
      )}

      {tab === 'Roster' && (
        <DataTable columns={[
          { key: 'work_date', header: 'Date', sortable: true },
          { key: 'employee_name', header: 'Employee', render: (r: RosterEntry) => r.employee_name ?? '—' },
          { key: 'start', header: 'Shift', render: (r: RosterEntry) => `${r.start}–${r.end}` },
          { key: 'role_key', header: 'Role', render: (r: RosterEntry) => r.role_key ?? '—' },
          { key: 'status', header: 'Status', render: (r: RosterEntry) => <StatusBadge status={r.status} /> },
          ...(canRoster ? [{
            key: 'act', header: '', render: (r: RosterEntry) =>
              r.status === 'scheduled' || r.status === 'swapped'
                ? <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => void act(() => http.post(`/staff/roster/${r.id}/status`, { status: 'off' }), 'Marked off')}>Off</Button>
                    <Button size="sm" variant="secondary" onClick={() => void act(() => http.post(`/staff/roster/${r.id}/status`, { status: 'cancelled' }), 'Cancelled')}>Cancel</Button>
                  </div>
                : null,
          }] as Column<RosterEntry>[] : []),
        ]} rows={roster} loading={loading} />
      )}

      {tab === 'Attendance' && (
        <DataTable columns={[
          { key: 'work_date', header: 'Date', sortable: true },
          { key: 'employee_name', header: 'Employee', render: (r: Attendance) => r.employee_name ?? '—' },
          { key: 'check_in', header: 'In', render: (r: Attendance) => r.check_in ? dateTime(r.check_in) : '—' },
          { key: 'check_out', header: 'Out', render: (r: Attendance) => r.check_out ? dateTime(r.check_out) : '—' },
          { key: 'status', header: 'Status', render: (r: Attendance) => <StatusBadge status={r.status} /> },
        ]} rows={attendance} loading={loading} />
      )}

      {tab === 'Payroll' && (
        <>
          {canPayroll && (
            <div className="flex justify-end">
              <Button variant="secondary" onClick={() => {
                const to = new Date(); const from = new Date(); from.setDate(1);
                void act(() => http.post('/staff/payroll', {
                  periodStart: from.toISOString().slice(0, 10), periodEnd: to.toISOString().slice(0, 10),
                }), 'Payroll run generated');
              }}><Plus size={16} /> Generate run</Button>
            </div>
          )}
          <DataTable columns={[
            { key: 'period_start', header: 'Period', render: (r: PayrollRun) => `${r.period_start} → ${r.period_end}` },
            { key: 'status', header: 'Status', render: (r: PayrollRun) => <StatusBadge status={r.status} /> },
            { key: 'total_net', header: 'Net', render: (r: PayrollRun) => money(r.total_net ?? 0) },
            ...(canPayroll ? [{
              key: 'act', header: '', render: (r: PayrollRun) => r.status !== 'approved'
                ? <div className="flex justify-end"><Button size="sm" variant="secondary" onClick={() => void act(() => http.post(`/staff/payroll/${r.id}/approve`), 'Payroll approved')}>Approve</Button></div>
                : null,
            }] as Column<PayrollRun>[] : []),
          ]} rows={runs} loading={loading} />
        </>
      )}

      {empModal && <EmployeeModal onClose={() => setEmpModal(false)} onSaved={() => { setEmpModal(false); void load(); }} />}
      {rosterModal && <RosterModal employees={employees} onClose={() => setRosterModal(false)} onSaved={() => { setRosterModal(false); void load(); }} />}
    </div>
  );
}

function EmployeeModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const toast = useUi((s) => s.toast);
  const [fullName, setFullName] = useState('');
  const [designation, setDesignation] = useState('');
  const [employmentType, setEmploymentType] = useState('full_time');
  const [hourly, setHourly] = useState('');
  const [salary, setSalary] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await http.post('/staff/employees', {
        fullName, designation: designation || null, employmentType,
        hourlyRate: Math.round(parseFloat(hourly || '0') * 100),
        baseSalary: Math.round(parseFloat(salary || '0') * 100),
      });
      toast({ kind: 'success', title: 'Employee added' });
      onSaved();
    } catch (e) { toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="New employee"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={() => void save()}>Save</Button></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Full name" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
        <Input label="Designation" value={designation} onChange={(e) => setDesignation(e.target.value)} />
        <Select label="Type" value={employmentType} onChange={(e) => setEmploymentType(e.target.value)}>
          <option value="full_time">Full time</option><option value="part_time">Part time</option>
          <option value="contract">Contract</option><option value="intern">Intern</option>
        </Select>
        <Input label="Hourly rate (£)" type="number" step="0.01" value={hourly} onChange={(e) => setHourly(e.target.value)} />
        <Input label="Monthly salary (£)" type="number" step="0.01" value={salary} onChange={(e) => setSalary(e.target.value)} />
      </div>
    </Modal>
  );
}

function RosterModal({ employees, onClose, onSaved }: { employees: Employee[]; onClose: () => void; onSaved: () => void }) {
  const toast = useUi((s) => s.toast);
  const [employeeId, setEmployeeId] = useState(employees[0]?.id ?? '');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [start, setStart] = useState('10:00');
  const [end, setEnd] = useState('18:00');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await http.post('/staff/roster', { employeeId, workDate: date, start, end });
      toast({ kind: 'success', title: 'Shift rostered' });
      onSaved();
    } catch (e) { toast({ kind: 'error', title: 'Failed', message: e instanceof ApiError ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Roster a shift"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={() => void save()}>Add</Button></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Select label="Employee" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
          {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
        </Select>
        <Input label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <Input label="Start" type="time" value={start} onChange={(e) => setStart(e.target.value)} />
        <Input label="End" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
      </div>
      <p className="mt-2 text-xs text-ink-muted"><Badge tone="info">tip</Badge> Tip distribution lives under Payroll → <code>POST /api/staff/tips/distribute</code>.</p>
    </Modal>
  );
}
